import React, { useState } from 'react';
import { Cpu, Layout, Server, Cloud, Database, ShoppingBag, Layers } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';
import { Skill } from '../types/portfolio';

export const Skills: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: 'All Tech', icon: Layers },
    { id: 'frontend', label: 'Frontend & Mobile', icon: Layout },
    { id: 'backend', label: 'Backend & APIs', icon: Server },
    { id: 'cloud', label: 'Cloud & DevOps', icon: Cloud },
    { id: 'database', label: 'Databases', icon: Database },
    { id: 'cms', label: 'CMS & E-Commerce', icon: ShoppingBag },
  ];

  const filteredSkills =
    activeCategory === 'all'
      ? portfolioData.skills
      : portfolioData.skills.filter((skill) => skill.category === activeCategory);

  return (
    <section id="skills" className="section skills-section">
      <div className="container">
        <div className="section-header">
          <div className="section-badge">
            <Cpu size={14} />
            <span>Technical Mastery</span>
          </div>
          <h2 className="section-title">
            Skills & <span className="text-gradient">Technologies</span>
          </h2>
          <p className="section-subtitle">
            Over a decade of deep expertise across modern frontend frameworks, distributed backends, cloud microservices, and database engineering.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="skill-tabs">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                className={`tab-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                <Icon size={16} />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        {/* Skills Grid */}
        <div className="skills-grid">
          {filteredSkills.map((skill: Skill) => (
            <div key={skill.name} className="skill-card glass-card">
              <div className="skill-info">
                <span className="skill-name">{skill.name}</span>
                <span className="skill-percentage">{skill.percent}%</span>
              </div>
              <div className="progress-bar-bg">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${skill.percent}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .skills-section {
          background: var(--bg-primary);
        }

        .skill-tabs {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          margin-bottom: 3rem;
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.65rem 1.25rem;
          border-radius: var(--radius-full);
          background: var(--bg-tertiary);
          border: 1px solid var(--border-color);
          color: var(--text-muted);
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .tab-btn:hover {
          color: var(--text-main);
          border-color: var(--border-highlight);
        }

        .tab-btn.active {
          background: var(--gradient-brand);
          color: #ffffff;
          border-color: transparent;
          box-shadow: var(--shadow-glow);
        }

        .skills-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.25rem;
        }

        .skill-card {
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
        }

        .skill-info {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .skill-name {
          font-weight: 700;
          font-size: 0.98rem;
          color: var(--text-main);
        }

        .skill-percentage {
          font-family: var(--font-mono);
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--accent-secondary);
        }

        .progress-bar-bg {
          width: 100%;
          height: 8px;
          border-radius: 4px;
          background: var(--bg-tertiary);
          overflow: hidden;
        }

        .progress-bar-fill {
          height: 100%;
          border-radius: 4px;
          background: var(--gradient-brand);
          transition: width 0.6s ease-out;
        }

        @media (max-width: 576px) {
          .skills-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};
